import React, { useState } from 'react';
import { Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { InfoCircleFilled, SyncOutlined } from '@ant-design/icons';
import { PATH } from 'constants/path';
import NewMessagePanel from 'components/NewMessagePanel';
import { ERoleCode } from 'types/enumItems';
import type { BoardSummaryVo, TInsuranceBillingStatus } from 'types/insuranceBilling';
import s from './s.module.less';

// "Message these providers" 的预填正文（原型 MSG_PREFILL，PLACEHOLDER 待 PM 确认）
const MSG_PREFILL = {
    notes:
        'Hi! A few of your Klarity Health visit notes still need to be locked before Klarity can bill them. ' +
        'Locking a note is quick, and the claim goes out automatically once it is done.\n\nThanks!',
    cpt:
        'Hi! A few of your services are still missing their CPT code, place of service, and modifiers, which ' +
        'Klarity needs to bill your Klarity Health appointments. Adding them only takes a minute.\n\nThanks!',
};

// index.tsx 从 onboarding VO（claimSettings）+ ehr-connections 推出的 EHR 侧状态
export type WaitingEhrState = {
    // billingEhrs 里受支持的 EHR（含 Klarity Health）
    supported: string[];
    // billingEhrs 里的自建 EHR（isCustom，等 Klarity 人工对接）
    pending: string[];
    klarityConnected: boolean;
    // 受支持且非 Klarity 的 EHR（waiting 期间视为「还在同步中」）
    others: string[];
};

type Props = {
    ehr: WaitingEhrState;
    // /status 的 4 个阻塞布尔在这上面（null = 未知，不渲染对应提示）
    status: TInsuranceBillingStatus | null;
    isGroup: boolean;
    // owner / 代持 owner 的 admin staff（teamNote 只给这类人看）
    canManage: boolean;
    // Step 6 存下来的计费范围，wow panel 底部那行说明用它（'all' | 'tagged'）
    claimScope?: string | null;
    // wow panel 的四个统计框与扣留时长（summary.visits / readyDelayHours）；null = 还没回来
    summary: BoardSummaryVo | null;
    // 打开 Settings › EHR & billing scope
    onConnectEhr: () => void;
    // 打开 To-do list（Home 的 TodoListModal，appointmentOnly 口径）
    onReviewNotes: () => void;
    // 打开 Bill for more patients 弹窗（放宽计费范围）
    onBillMore: () => void;
    // wow panel 的出口：把面板换成装着这些 visit 的 claims 表格，面板不再回来
    onViewAll: () => void;
};

// wow panel 的四个统计框 + 放宽计费范围的入口（原型 wfsStrip / wowMore）。
// 起步时把范围定窄（只计费打了标签的患者、或只从今天起算）的 provider，看到第一批 claim
// 落地后往往想放宽，这里给入口就不必绕回 Settings。
// 10/05：统计框下面加 View all results —— 离开面板的路就是往前走一步：这些数字数的那些 visit，
// 以表格行的形式摆出来。点过一次面板就不再出现（首屏惊喜只给一次，表格逐条说的是同一件事）。
// 数字来自 summary.visits：total 含窗口外的 visit（outsideWindow），所以四个框加起来不等于 total。
// 第三个框不写扣留时长（是配置值，不一定是 2 小时）。
// TODO(PM): 窗口外 visit 的说明行（"N visits are before your billing start date…"）原型没有，先不展示，等产品确认再加
type WowStatsProps = {
    visits: BoardSummaryVo['visits'] | null;
    claimScope?: string | null;
    onBillMore: () => void;
    onViewAll: () => void;
};
const WowStats = ({ visits, claimScope, onBillMore, onViewAll }: WowStatsProps) => {
    const boxes = [
        { n: visits?.total, l: 'billable visits found' },
        { n: visits?.ready, l: 'ready to submit' },
        { n: visits?.preparing, l: 'being prepared' },
        { n: visits?.notReady, l: 'waiting on info' },
    ];
    return (
        <>
            <div className={s.gsWfs}>
                {boxes.map((b) => (
                    <div key={b.l} className={s.gsWfsCard}>
                        <div className={s.gsWfsNum}>{b.n ?? '—'}</div>
                        <div className={s.gsWfsLabel}>{b.l}</div>
                    </div>
                ))}
            </div>
            {/* 没有任何 visit 时没有东西可看，不给出口 */}
            {!!visits?.total && (
                <div className={`${s.gsCta} ${s.gsSeeCta}`}>
                    <Button type="primary" size="large" className={`${s.gsSeeBtn} ${s.blueBtn}`} onClick={onViewAll}>
                        View all results
                    </Button>
                </div>
            )}
            <div className={s.gsMore}>
                <span className={s.gsMoreTxt}>
                    {claimScope === 'tagged'
                        ? 'Klarity is only billing for the patients you tagged.'
                        : 'Klarity is billing every patient from your start date.'}
                </span>
                <Button type="link" className={s.gsMoreLink} onClick={onBillMore}>
                    Bill for more patients&nbsp;<span aria-hidden="true">→</span>
                </Button>
            </div>
        </>
    );
};

// 已激活但还没有 claims 的 waiting 态（原型 emptyHtml()）：标题永远说「正在发生什么」，
// 变体按 Klarity 链路的断点排优先级：无 EHR → 等我们支持 → services 没配 → note 没提交 →
// preparing 兜底；没连 Klarity 一律 "You are all set"。第三方 EHR 永远只是底部一行灰字。
// 文案不带数字：/status 只给布尔（"Some of yours…"），未知（null）当无阻塞落到下一分支
const WaitingCard = ({
    ehr,
    status,
    isGroup,
    canManage,
    claimScope,
    summary,
    onConnectEhr,
    onReviewNotes,
    onBillMore,
    onViewAll,
}: Props) => {
    const navigate = useNavigate();
    // Message these providers 弹窗（复用 TeamMessageModal，收件人只列 provider）
    const [msgOpen, setMsgOpen] = useState(false);
    // 两个 currentProvider* 字段为 null = 调用者不在 rendering 名册（不出诊的 owner/admin）：
    // 个人视角的数据对他没有意义，给团队视角文案（后端文档 ⚠️②；任一非 null 即出诊，OR 兜半残数据）
    const viewerRenders =
        status?.currentProviderServiceTemplatePending != null || status?.currentProviderTreatmentPlanPending != null;
    // 四个字段同向（都以 Pending 结尾）：=== true 才是「有阻塞、要提示」，null 落不进任何一支
    const selfSvcMissing = status?.currentProviderServiceTemplatePending === true;
    const selfNotesPending = status?.currentProviderTreatmentPlanPending === true;
    const teamSvcMissing = status?.teamServiceTemplatePending === true;
    const teamNotesPending = status?.teamTreatmentPlanPending === true;
    // teamNote 蓝横幅只给 group 的 owner / admin staff：团队 provider 不为别人负责
    const showTeam = isGroup && canManage;

    // 无 EHR：允许提交时不连（EHR 非必选），这里说清代价 + 唯一能做的动作。
    // Connect an EHR 开的是 Settings › EHR & billing scope，team provider（canManage=false）
    // 无权改 billing 设置，只给说明不给按钮
    if (!ehr.supported.length && !ehr.pending.length) {
        return (
            <div className={s.gettingStarted}>
                <div className={s.gsTitle}>Connect an EHR to start billing</div>
                <div className={s.gsBody}>
                    Your billing setup is saved. <b>Klarity creates claims from the appointments in your EHR</b>, so
                    nothing is billed until one is connected.
                </div>
                {canManage && (
                    <div className={s.gsCta}>
                        <Button size="large" className={s.gsCtaBtn} onClick={onConnectEhr}>
                            Connect an EHR
                        </Button>
                    </div>
                )}
            </div>
        );
    }

    // 只登记了我们还不支持的 EHR：完成 onboarding ≠ 激活，没有任何东西在扫描
    if (!ehr.supported.length) {
        const name = ehr.pending.join(', ');
        return (
            <div className={s.gettingStarted}>
                <div className={s.gsTitle}>Waiting on support for {name}</div>
                <div className={s.gsBody}>
                    Your billing setup is saved, and our team will reach out about <b>{name}</b>.{' '}
                    <b>Klarity can only bill from a connected EHR</b>, so no claims are created until then.
                </div>
                {canManage && (
                    <div className={s.gsCta}>
                        <Button size="large" className={s.gsCtaBtn} onClick={onConnectEhr}>
                            Connect an EHR
                        </Button>
                        <p className={s.gsNote}>
                            Already using Klarity Health, SimplePractice, Tebra, Athena, Healthie, IntakeQ, or
                            Optimantra? Connect it in <b>Settings &rsaquo; EHR &amp; billing scope</b> and Klarity can
                            start billing those patients today.
                        </p>
                    </div>
                )}
            </div>
        );
    }

    let title: React.ReactNode;
    let body: React.ReactNode;
    let cta: React.ReactNode = null;
    let teamNote: string | null = null;
    // teamNote 的动作按钮上下文：决定 Message these providers 弹窗的预填正文
    let msgContext: keyof typeof MSG_PREFILL | null = null;
    let showSpin = false;
    // wow panel（下面第 5 支）：四个统计框 + 放宽范围的入口。它一出现，底部那行
    // "still connecting" 就不再渲染 —— 统计框已经把各 EHR 的 visit 都算进去了，
    // 再说一句"还在连"会跟上面的数字互相矛盾
    let showWowPanel = false;

    if (ehr.klarityConnected && (viewerRenders ? selfSvcMissing : teamSvcMissing)) {
        // services 缺 CPT/POS/modifiers：claim line 根本建不出来
        if (viewerRenders) {
            title = 'Set up your services to start billing';
            body = (
                <>
                    Klarity bills your Klarity Health appointments automatically, but a claim needs a{' '}
                    <b>CPT code, place of service, and modifiers</b> on each service. Some of yours are still missing
                    them.
                </>
            );
            cta = (
                <Button size="large" className={s.gsCtaBtn} onClick={() => navigate(`/${PATH.PPA_SERVICE_TEMPLATE}`)}>
                    Set up now
                </Button>
            );
        } else {
            // 不出诊的 owner/admin：这里没有一件事是他能替别人完成的
            title = 'Your providers set up their own services';
            body = (
                <>
                    Klarity bills a Klarity Health appointment automatically, but a claim needs a{' '}
                    <b>CPT code, place of service, and modifiers</b> on the provider&apos;s services.{' '}
                    <b>Some providers</b> in the practice have not added them yet.
                </>
            );
        }
        if (showTeam && teamSvcMissing) {
            teamNote =
                'Each provider adds their own service codes, and appointments stay unbilled until they do. ' +
                'Notify your team to keep billing moving.';
            msgContext = 'cpt';
        }
    } else if (ehr.klarityConnected && (viewerRenders ? selfNotesPending : teamNotesPending)) {
        // note 未提交：预约发生了、claim 不出现，不说的话就是无声的阻塞
        if (viewerRenders) {
            title = 'Klarity is billing automatically';
            body = (
                <>
                    A claim is created as soon as you lock a visit note. <b>Some of your Klarity Health appointments</b>{' '}
                    are waiting on one.
                </>
            );
            cta = (
                <Button size="large" className={s.gsCtaBtn} onClick={onReviewNotes}>
                    Review pending notes
                </Button>
            );
        } else {
            title = 'Waiting on your providers to lock their notes';
            body = (
                <>
                    A claim is created as soon as the visit note is locked. <b>Some Klarity Health appointments</b>{' '}
                    across the practice are waiting on one.
                </>
            );
        }
        if (showTeam && teamNotesPending) {
            teamNote =
                'Each provider locks their own notes, and appointments stay unbilled until they do. ' +
                'Notify your team to keep billing moving.';
            msgContext = 'notes';
        }
    } else {
        // 兜底支：至少连着一个受支持的 EHR（上面两个 early return 已经挡掉了没有 EHR 的情况），
        // services 配齐了、note 也都锁了，还是没有 claim —— 那就是处理延迟。
        // 09/10 起这一支换成 wow panel：与其说「在准备中」，不如把扫描结果亮出来 ——
        // 找到多少次可计费的就诊、多少能直接发、多少还在扣留期、多少在等资料。
        // 不限 Klarity：第三方 EHR 同样扫得到，统计框算的是当前连上的全部 EHR
        //（所以这一支下面不再渲染「still connecting」灰字，否则和这些数字互相矛盾）。
        // 后端算不出准确的出单 ETA（readyDelayHours 只是扣留期配置），所以标题不承诺时间，
        // 正文时长写成下限（at least），且只对「准备好了」的 visit 说会发出 ——
        // waiting on info / 窗口外的 visit 不一定会发。TODO(PM): 标题与正文文案待 PM 确认
        // TODO(backend): 扣留期起点待确认（就诊时间 / EHR 同步 / note 锁定），不是就诊时间的话 "after it happens" 要改
        showSpin = true;
        showWowPanel = true;
        title = 'Klarity is preparing your first claims';
        const hold = summary?.readyDelayHours;
        body =
            hold != null
                ? `Klarity found these in your connected EHR. Each visit is held for at least ${hold} ${
                      hold === 1 ? 'hour' : 'hours'
                  } after it happens, then goes out on a later submission cycle once it's ready.`
                : "Klarity found these in your connected EHR. Each visit is held for a short time after it happens, then goes out on a later submission cycle once it's ready.";
    }

    return (
        <div className={s.gettingStarted}>
            {showSpin && (
                <div className={s.gsSpinWrap}>
                    <SyncOutlined spin className={s.gsSpinLarge} />
                </div>
            )}
            <div className={s.gsTitle}>{title}</div>
            <div className={s.gsBody}>{body}</div>
            {showWowPanel && (
                <WowStats
                    visits={summary?.visits ?? null}
                    claimScope={claimScope}
                    onBillMore={onBillMore}
                    onViewAll={onViewAll}
                />
            )}
            {cta && <div className={s.gsCta}>{cta}</div>}
            {teamNote && (
                <div className={`${s.infoBanner} ${s.gsTeam}`}>
                    <InfoCircleFilled className={s.infoBannerIcon} />
                    <span className={s.infoBannerText}>{teamNote}</span>
                    {/* 动作按钮长在解释它的横幅上（原型 msgActionHtml），预填文案按上下文切换 */}
                    {msgContext && (
                        <button type="button" className={s.infoBannerAction} onClick={() => setMsgOpen(true)}>
                            Message these providers <span aria-hidden="true">&rarr;</span>
                        </button>
                    )}
                </div>
            )}
            {ehr.others.length > 0 && !showWowPanel && (
                <p className={s.gsOther}>
                    <SyncOutlined spin className={s.gsSpin} />
                    <span>
                        <b>{ehr.others.join(', ')}</b>{' '}
                        {ehr.others.length > 1
                            ? 'are still connecting, usually a few hours. Those claims appear as each one finishes.'
                            : 'is still connecting, usually a few hours. Those claims appear when it finishes.'}
                    </span>
                </p>
            )}
            {msgContext && (
                <NewMessagePanel
                    open={msgOpen}
                    onClose={() => setMsgOpen(false)}
                    prefillMessage={MSG_PREFILL[msgContext]}
                    initialSendTo="team"
                    recipientRoles={[ERoleCode.GROUP_PROVIDER]}
                />
            )}
        </div>
    );
};

export default WaitingCard;
